<template>
    <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
            <div class="seat-tile-wrapper" v-bind="attrs" v-on="on">
                <div class="seat-name">{{ assignment && assignment.user ? displayName : '' }}</div>
                <button type="button" class="seat-tile" :class="{
                        'seat-tile--occupied': !!assignment,
                        'seat-tile--draggable': isOccupied,
                        'seat-tile--drop-target': dragOver,
                        'seat-tile--tall': tall,
                    }" :style="tileStyle" :draggable="isOccupied" @click="$emit('click')"
                    @dragstart="onDragStart" @dragend="onDragEnd" @dragover="onDragOver"
                    @dragenter="onDragEnter" @dragleave="onDragLeave" @drop="onDrop">
                    <template v-if="assignment && assignment.user">
                        <v-avatar size="50" ref="avatar">
                            <v-img v-if="assignment.user.profile_picture" :src="getProfilePictureUrl(assignment.user.profile_picture)" :aspect-ratio="1" class="seat-avatar-img"></v-img>
                            <v-avatar v-else color="primary" size="50" class="white--text text-caption">{{ initials }}</v-avatar>
                        </v-avatar>
                    </template>
                    <v-icon v-else :color="dragOver ? 'primary' : 'grey lighten-1'">{{ dragOver ? 'mdi-plus' : icon }}</v-icon>
                </button>
            </div>
        </template>
        <span>{{ assignment && assignment.user ? assignment.user.name : `Seat ${seat.label} - Available` }}</span>
    </v-tooltip>
</template>

<script>
import { getUnit, unitColor } from '../unitColors';

export default {
    name: 'SeatTile',
    props: {
        seat: { type: Object, required: true },
        assignment: { type: Object, default: null },
        tall: { type: Boolean, default: false },
        icon: { type: String, default: 'mdi-plus' },
    },
    data() {
        return {
            dragOver: false,
        };
    },
    computed: {
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        isOccupied() {
            return !!(this.assignment && this.assignment.user);
        },
        initials() {
            const user = this.assignment && this.assignment.user;
            if (!user || !user.name) return '?';
            const names = user.name.split(' ');
            return names.length >= 2 ? (names[0][0] + names[1][0]).toUpperCase() : user.name.substring(0, 2).toUpperCase();
        },
        displayName() {
            const user = this.assignment && this.assignment.user;
            if (!user) return '';
            if (user.first_name && user.last_name) {
                return `${user.first_name} ${user.last_name}`;
            }
            return user.name || '';
        },
        seatUnitColor() {
            const user = this.assignment && this.assignment.user;
            return unitColor(getUnit(user));
        },
        tileStyle() {
            if (this.seatUnitColor) {
                return { borderColor: this.seatUnitColor, borderStyle: 'solid' };
            }
            if (this.assignment) {
                return { borderColor: '#bdbdbd', borderStyle: 'solid' };
            }
            return {};
        },
    },
    methods: {
        getProfilePictureUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${this.apiUrl}/${path}`;
        },
        onDragStart(e) {
            if (!this.isOccupied) return;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', this.seat.key);
            if (this.$refs.avatar && this.$refs.avatar.$el && e.dataTransfer.setDragImage) {
                e.dataTransfer.setDragImage(this.$refs.avatar.$el, 25, 25);
            }
            this.$emit('seat-dragstart', this.seat.key);
        },
        onDragEnd() {
            this.dragOver = false;
            this.$emit('seat-dragend');
        },
        onDragOver(e) {
            if (this.isOccupied) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        },
        onDragEnter(e) {
            if (this.isOccupied) return;
            e.preventDefault();
            this.dragOver = true;
        },
        onDragLeave() {
            this.dragOver = false;
        },
        onDrop(e) {
            if (this.isOccupied) return;
            e.preventDefault();
            this.dragOver = false;
            const fromKey = e.dataTransfer.getData('text/plain');
            this.$emit('seat-drop', { from: fromKey, to: this.seat.key });
        },
    },
};
</script>

<style scoped>
.seat-tile-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 56px;
}

.seat-name {
    width: 56px;
    height: 22px;
    font-size: 0.6rem;
    line-height: 1.1;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    text-align: center;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    margin-bottom: 2px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;
}

.seat-tile {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    border: 2px dashed #d0d0d0;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    padding: 0;
}

.seat-tile--tall {
    /* Spans the height of two stacked seat rows plus the row gap between them. */
    height: 116px;
}

.seat-tile * {
    /* Keep drag events (dragenter/dragleave/dragover/drop) targeted at the
       button itself, not its icon/avatar children - otherwise hovering
       dead-center over a child triggers a leave+enter flicker that drops
       the drop-target highlight. */
    pointer-events: none;
}

.seat-tile:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
}

.seat-tile--occupied {
    background: #ffffff;
    border-width: 2px;
}

.seat-tile--draggable {
    cursor: grab;
}

.seat-tile--draggable:active {
    cursor: grabbing;
}

.seat-tile--drop-target {
    background: #e3f2fd;
    border-color: #1976d2 !important;
    border-style: solid !important;
    box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.25);
    transform: scale(1.08);
}
</style>
